import { Link } from "react-router";
import Button from "@components/Button/Button";
import { ActionType, ButtonSize } from "@components/Button/utils/types";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import { TrackingObject } from "@features/analytics/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { CardVariations } from "@particles/card/utils/types";
import ArticleByline from "@routes/Article/components/ArticleByline/ArticleByline";
import {
  formatAudioDuration,
  formatPublishedDate,
} from "@routes/Article/utils/helpers";
import { PodcastDataType } from "@routes/Article/utils/types";
import {
  Track,
  useStickyAudioPlayer,
  useTrackPlayback,
} from "@sphtech/web2-reusable-components";
import { cn, getFormattedUrlPath } from "@util/helpers";

import PodcastSubscriptionsComponent from "./PodcastSubscriptionsComponent";

type PodcastHeroComponentProps = {
  podcast: PodcastDataType;
  title: string;
  blurb: string;
  publishTime: string;
  shortUrl: string;
};

/**
 * Component for Podcast Article Hero Section, which will display the podcast title, blurb, and a play button to play the podcast. It will also display the subscribe links for the podcast.
 * https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=4844-133248&m=dev
 */
const PodcastHeroComponent = ({
  podcast,
  title,
  blurb,
  publishTime,
  shortUrl,
}: PodcastHeroComponentProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const sendDataToGTM = useSendDataToGTM();
  const { state } = useStickyAudioPlayer();

  const { track, currentTime, isPlaying } = state;
  const { externalLinks, imageUrl, urlPath, name } = podcast.tag;

  const tagUrlPath = getFormattedUrlPath(urlPath, [
    { key: "ref", value: "podcast-article-top" },
  ]);

  const audioTrack: Track = {
    audioTitle: title,
    audioUrl: podcast.url,
    audioBannerImage: imageUrl,
    audioPublishedDate: publishTime,
    audioShareUrl: shortUrl,
  };

  const { handleTrack, isPlayingCurrentAudio } = useTrackPlayback({
    title: title,
    trackData: audioTrack,
  });

  const podcastHost = podcast?.host;
  const variations: CardVariations = {
    byline: {
      imageWidth: isScreenLG ? 52 : 30,
      imageHeight: isScreenLG ? 52 : 30,
      linkClassName: "!text-gray-175 hidden lg:block",
    },
  };
  const trackingObject: TrackingObject = {
    audio_auto_played: "no",
    audio_title: title?.toLowerCase() || "",
    audio_current_time: currentTime?.toString() || "0",
    audio_duration: podcast?.duration?.toString() || "0",
    cd_language: "en",
    audio_player: "main",
    audio_published_date: formatPublishedDate(publishTime) || "",
    audio_category: "",
    audio_percent: "",
    contenttype: "podcast article",
    event: "audio_start",
  };

  const handlePlay = () => {
    const tracker = {
      ...trackingObject,
      event: "audio_start",
      audio_current_time: "0",
    };

    if (!track || track.audioUrl !== audioTrack.audioUrl) {
      sendDataToGTM(tracker);
      handleTrack();

      return;
    }

    sendDataToGTM({
      ...trackingObject,
      event: isPlaying ? "audio_pause" : "audio_play",
    });
    handleTrack();
  };

  return (
    <div
      data-testid="podcast-hero-component"
      className="mb-6 bg-white bg-[linear-gradient(0deg,_rgba(0,0,0,0.20)_0%,_rgba(0,0,0,0.20)_100%),linear-gradient(180deg,_#2561CF_0%,_#000_100%)]"
    >
      <div className="flex items-center justify-between bg-black/10 px-4 py-3 font-poppins md:bg-transparent md:px-8 md:pb-0 md:pt-8">
        <div className="flex flex-row items-center gap-4">
          <Link to={tagUrlPath.toString()} reloadDocument>
            <img
              src={imageUrl}
              alt={title}
              width={isScreenLG ? 100 : 42}
              height={isScreenLG ? 100 : 42}
            />
          </Link>

          {podcastHost ? (
            <Link to={podcastHost.urlPath} target="_blank">
              <ArticleByline
                authors={[podcastHost]}
                updatedTime=""
                variations={variations}
              />
            </Link>
          ) : null}
        </div>

        <div className="flex flex-row items-center">
          <div className="hidden text-xs text-blue-165 lg:block">
            Subscribe:
          </div>

          <div className="flex flex-row items-center gap-4 lg:gap-0 lg:divide-x">
            {externalLinks.map((link) => (
              <PodcastSubscriptionsComponent
                key={link.title}
                link={link}
                tagName={name}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex flex-col items-center gap-3 px-6 py-8 text-center md:max-w-[572px] md:gap-4 md:py-24">
        <h1
          data-testid="article-title"
          className={cn(
            "font-lct text-4xl font-bold leading-normal -tracking-2% text-white md:font-semibold lg:text-8xl"
          )}
        >
          {title}
        </h1>

        <p
          className={cn(
            "mb-0 line-clamp-2 font-lucida text-xs font-medium text-gray-175 lg:text-xl"
          )}
        >
          {blurb}
        </p>

        <Button
          buttonSize={ButtonSize.medium}
          className="rounded-full border-0 bg-white px-6 py-10"
          onClick={handlePlay}
          actionType={ActionType.actionButton}
          testId="audio-button-component"
        >
          <IconFactory
            type={
              isPlayingCurrentAudio ? IconTypeEnum.pause : IconTypeEnum.play
            }
            size={30}
            x={isPlayingCurrentAudio ? 35 : 20}
            y={30}
            className="text-black"
          />
        </Button>

        <div className="flex flex-row items-center gap-1 font-poppins text-xs text-gray-175">
          <span>Listen</span>
          <span className="pb-2">.</span>
          <span>{formatAudioDuration(podcast?.duration || 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default PodcastHeroComponent;
